/*
 * DEPRECADO (Asambleas Fase 2): pantalla vieja "actaaudio", reemplazada por
 * actaAdmin + asambleas-worker (sección Acta del detalle de la asamblea y Editor
 * de Actas). Se retira en la limpieza posterior, no agregar funcionalidad.
 * Usa /api/process-meeting-audio, /api/structure-meeting y /api/meetings/*
 * (también deprecados); nunca lee `meetings` directo.
 */
import React, { useEffect, useState } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { HABI_API_BASE_URL, getAuthHeaders } from "../../utils/habiApi";
import {
  colors,
  spacing,
  typography,
  radius,
  cardBase,
  pageContainer,
  pageTitle,
  btnPrimary,
  btnSecondary,
  btnGhost,
} from "../../utils/designTokens";
import ScreenHeader from "../../components/ScreenHeader";

/**
 * B3 — Pantalla de Asambleas: subir audio o pegar transcripcion, recibir
 * acta estructurada generada por IA segun ley UY 10.751. Las actas se
 * guardan en Firestore (collection meetings) y se pueden recuperar despues.
 *
 * MVP: sin diarizacion (Whisper no la incluye). Audio < 25MB (~30 min).
 * Audios mas largos: el staff los divide o usa "Pegar transcripcion".
 */
export default function Asambleas({ setSelected }) {
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();

  const [audio, setAudio] = useState(null);
  const [transcriptionText, setTranscriptionText] = useState("");
  const [mode, setMode] = useState("audio"); // audio | text

  // Campos del acta
  const [meetingNumber, setMeetingNumber] = useState("");
  const [meetingType, setMeetingType] = useState("ordinaria");
  const [razonSocial, setRazonSocial] = useState("");
  const [attendees, setAttendees] = useState(""); // textarea, uno por linea
  const [agenda, setAgenda] = useState(""); // textarea, uno por linea

  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null); // { meeting_id, minutes_markdown, transcription_text }
  const [pastMeetings, setPastMeetings] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (selectedBuilding) loadPastMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding]);

  const loadPastMeetings = async () => {
    try {
      setLoadingHistory(true);
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${HABI_API_BASE_URL}/api/meetings/${selectedBuilding.buildingid}?limit=20`,
        { headers }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      setPastMeetings(data.items || []);
    } catch (e) {
      console.warn("[B3] no se pudieron cargar actas previas:", e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openPastMeeting = async (mtgId) => {
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${HABI_API_BASE_URL}/api/meetings/${selectedBuilding.buildingid}/${mtgId}`,
        { headers }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      setResult({
        meeting_id: data.id,
        minutes_markdown: data.minutes_markdown,
        transcription_text: data.transcription_text,
      });
    } catch (e) {
      showToast(e.message || "No se pudo cargar el acta.", "error");
    }
  };

  const parseLines = (txt) =>
    (txt || "")
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);

  const processAudio = async () => {
    if (!selectedBuilding) {
      showToast("Seleccioná un edificio.", "warning");
      return;
    }
    if (!audio) {
      showToast("Subí un archivo de audio.", "warning");
      return;
    }
    if (audio.size > 25 * 1024 * 1024) {
      showToast("El audio supera 25MB. Dividilo en partes o pegá la transcripción.", "warning");
      return;
    }
    setProcessing(true);
    setResult(null);
    try {
      const headers = await getAuthHeaders();
      // No mandar Content-Type — FormData lo setea solo con el boundary.
      delete headers["Content-Type"];
      const fd = new FormData();
      fd.append("audio", audio);
      fd.append("building_id", String(selectedBuilding.buildingid));
      if (meetingNumber) fd.append("meeting_number", meetingNumber);
      if (meetingType) fd.append("meeting_type", meetingType);
      if (razonSocial) fd.append("razon_social", razonSocial);
      const att = parseLines(attendees);
      if (att.length) fd.append("expected_attendees", JSON.stringify(att));
      const ag = parseLines(agenda);
      if (ag.length) fd.append("agenda", JSON.stringify(ag));

      const resp = await fetch(`${HABI_API_BASE_URL}/api/process-meeting-audio`, {
        method: "POST",
        headers,
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo procesar el audio.", "error");
        return;
      }
      const data = await resp.json();
      setResult(data);
      showToast("Acta generada. Revisá antes de firmar.", "success");
      loadPastMeetings();
    } catch (e) {
      showToast(e.message || "Error inesperado procesando el audio.", "error");
    } finally {
      setProcessing(false);
    }
  };

  const processText = async () => {
    if (!selectedBuilding) {
      showToast("Seleccioná un edificio.", "warning");
      return;
    }
    if (!transcriptionText.trim()) {
      showToast("Pegá la transcripción de la asamblea.", "warning");
      return;
    }
    setProcessing(true);
    setResult(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/structure-meeting`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(selectedBuilding.buildingid),
          transcription_text: transcriptionText,
          meeting_number: meetingNumber || null,
          meeting_type: meetingType || "ordinaria",
          razon_social: razonSocial || null,
          expected_attendees: parseLines(attendees).length ? parseLines(attendees) : null,
          agenda: parseLines(agenda).length ? parseLines(agenda) : null,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo estructurar la transcripción.", "error");
        return;
      }
      const data = await resp.json();
      setResult(data);
      showToast("Acta generada. Revisá antes de firmar.", "success");
      loadPastMeetings();
    } catch (e) {
      showToast(e.message || "Error inesperado.", "error");
    } finally {
      setProcessing(false);
    }
  };

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(result?.minutes_markdown || "");
      showToast("Acta copiada al portapapeles.", "success");
    } catch (e) {
      showToast("No se pudo copiar.", "error");
    }
  };

  if (!selectedBuilding) {
    return (
      <div style={pageContainer}>
        <ScreenHeader title="Asambleas" subtitle="Acta automática con IA" />
        <div style={{ ...cardBase, padding: 24, marginTop: 16 }}>
          <p style={{ color: colors.gray600 }}>Seleccioná un edificio para procesar asambleas.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageContainer}>
      <ScreenHeader
        title="Asambleas"
        subtitle={`Acta automática con IA · ${selectedBuilding.name || `Edificio ${selectedBuilding.buildingid}`}`}
      />

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 320px", gap: 16, marginTop: 16 }}>
        {/* COLUMNA PRINCIPAL */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Tabs de modo */}
          <div style={{ ...cardBase, padding: 16 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <button
                type="button"
                onClick={() => setMode("audio")}
                style={{
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 400,
                  color: mode === "audio" ? colors.white : colors.primary,
                  background: mode === "audio" ? colors.primary : colors.primaryLighter,
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                🎤 Subir audio
              </button>
              <button
                type="button"
                onClick={() => setMode("text")}
                style={{
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 400,
                  color: mode === "text" ? colors.white : colors.primary,
                  background: mode === "text" ? colors.primary : colors.primaryLighter,
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                📝 Pegar transcripción
              </button>
            </div>

            {/* Datos del acta (compartidos) */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <FormFieldSimple label="N° de acta" value={meetingNumber} onChange={setMeetingNumber} placeholder="Ej: 42" />
              <FormFieldSelect
                label="Tipo de asamblea"
                value={meetingType}
                onChange={setMeetingType}
                options={["ordinaria", "extraordinaria", "constituyente"]}
              />
              <FormFieldSimple label="Razón social del consorcio" value={razonSocial} onChange={setRazonSocial} placeholder="Ej: Consorcio Edificio Obligado" />
            </div>
            <FormFieldTextarea
              label="Lista de presentes (uno por línea)"
              value={attendees}
              onChange={setAttendees}
              rows={3}
              placeholder={"Juan Pérez (apto 101)\nMaría López (apto 203, 8% representación)"}
            />
            <FormFieldTextarea
              label="Orden del día (uno por línea)"
              value={agenda}
              onChange={setAgenda}
              rows={3}
              placeholder={"Aprobación balance 2025\nDecisión sobre obra fachada\nElección presidente comisión"}
            />

            {mode === "audio" ? (
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 13, color: colors.gray700, display: "block", marginBottom: 6 }}>
                  Archivo de audio (mp3, m4a, wav · máx. 25MB · ~30 min)
                </label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setAudio(e.target.files?.[0] || null)}
                  style={{ width: "100%" }}
                />
                {audio && (
                  <div style={{ fontSize: 12, color: colors.gray600, marginTop: 4 }}>
                    {audio.name} · {(audio.size / 1024 / 1024).toFixed(1)} MB
                  </div>
                )}
                <button
                  type="button"
                  onClick={processAudio}
                  disabled={processing || !audio}
                  style={{
                    ...btnPrimary,
                    marginTop: 12,
                    opacity: processing || !audio ? 0.6 : 1,
                    cursor: processing || !audio ? "not-allowed" : "pointer",
                  }}
                >
                  {processing ? "Procesando…" : "Transcribir y generar acta"}
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 12 }}>
                <FormFieldTextarea
                  label="Pegá aquí la transcripción de la asamblea"
                  value={transcriptionText}
                  onChange={setTranscriptionText}
                  rows={10}
                  placeholder="La asamblea inició a las 19:30hs. El presidente abrió la sesión..."
                />
                <button
                  type="button"
                  onClick={processText}
                  disabled={processing || !transcriptionText.trim()}
                  style={{
                    ...btnPrimary,
                    marginTop: 12,
                    opacity: processing || !transcriptionText.trim() ? 0.6 : 1,
                    cursor: processing || !transcriptionText.trim() ? "not-allowed" : "pointer",
                  }}
                >
                  {processing ? "Generando acta…" : "Estructurar acta"}
                </button>
              </div>
            )}
          </div>

          {/* Resultado */}
          {result?.minutes_markdown && (
            <div style={{ ...cardBase, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: typography.sizes.lg, fontWeight: 400, color: colors.gray900 }}>
                  Acta generada
                </h3>
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" onClick={copyMarkdown} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}>
                    Copiar markdown
                  </button>
                </div>
              </div>
              <div style={{
                padding: 14,
                background: colors.gray50,
                borderRadius: 8,
                whiteSpace: "pre-wrap",
                fontSize: 13,
                lineHeight: 1.55,
                color: colors.gray900,
                maxHeight: 600,
                overflowY: "auto",
                fontFamily: typography.fontFamily,
              }}>
                {result.minutes_markdown}
              </div>
              <p style={{ fontSize: 11, color: colors.gray600, marginTop: 8, fontStyle: "italic" }}>
                ⚠ Acta generada con asistencia IA. Revisar el contenido antes de firmar. La responsabilidad legal recae en la administración y el presidente de la asamblea.
              </p>
            </div>
          )}

          {result?.transcription_text && (
            <details style={{ ...cardBase, padding: 12 }}>
              <summary style={{ cursor: "pointer", fontSize: 13, color: colors.gray700 }}>
                Ver transcripción cruda
              </summary>
              <div style={{ marginTop: 8, fontSize: 12, color: colors.gray700, whiteSpace: "pre-wrap", maxHeight: 300, overflowY: "auto" }}>
                {result.transcription_text}
              </div>
            </details>
          )}
        </div>

        {/* SIDEBAR: actas anteriores */}
        <div style={{ ...cardBase, padding: 16, alignSelf: "flex-start" }}>
          <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400, color: colors.gray900 }}>
            Actas anteriores
          </h3>
          {loadingHistory && (
            <div style={{ fontSize: 12, color: colors.gray600, marginTop: 8 }}>Cargando…</div>
          )}
          {!loadingHistory && pastMeetings.length === 0 && (
            <div style={{ fontSize: 12, color: colors.gray600, marginTop: 8 }}>
              Aún no procesaste ninguna asamblea.
            </div>
          )}
          {pastMeetings.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => openPastMeeting(m.id)}
              style={{
                width: "100%",
                marginTop: 8,
                padding: 10,
                background: colors.gray50,
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                textAlign: "left",
                fontSize: 12,
                color: colors.gray900,
              }}
            >
              <div style={{ fontWeight: 400 }}>
                {m.meeting_number ? `N° ${m.meeting_number}` : "(sin número)"} · {m.meeting_type}
              </div>
              <div style={{ color: colors.gray600 }}>
                {m.processed_at ? new Date(m.processed_at).toLocaleString("es-UY") : "—"}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function FormFieldSimple({ label, value, onChange, placeholder }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: colors.gray700 }}>{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: "8px 10px",
          fontSize: 13,
          border: `1px solid ${colors.gray200}`,
          borderRadius: 8,
          fontFamily: typography.fontFamily,
        }}
        className="habitia-input"
      />
    </label>
  );
}

function FormFieldSelect({ label, value, onChange, options }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: colors.gray700 }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "8px 10px",
          fontSize: 13,
          border: `1px solid ${colors.gray200}`,
          borderRadius: 8,
          fontFamily: typography.fontFamily,
          background: "#fff",
        }}
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

function FormFieldTextarea({ label, value, onChange, rows, placeholder }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
      <span style={{ fontSize: 12, color: colors.gray700 }}>{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows || 3}
        placeholder={placeholder}
        style={{
          padding: 10,
          fontSize: 13,
          border: `1px solid ${colors.gray200}`,
          borderRadius: 8,
          fontFamily: typography.fontFamily,
          resize: "vertical",
        }}
      />
    </label>
  );
}
